/**
 * Select — styled wrapper over a native `<select>` element.
 * Same frosted-glass / coral-focus-ring family as Input.
 * Fully forwards all native `<select>` attributes.
 */
import { forwardRef, type SelectHTMLAttributes } from 'react';
import clsx from 'clsx';

export type SelectProps = SelectHTMLAttributes<HTMLSelectElement>;

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { className, children, ...props },
  ref,
) {
  return (
    <div className="relative w-full">
      <select
        ref={ref}
        className={clsx(
          // Base layout & shape
          'block w-full appearance-none rounded-lg px-3 py-2 pr-8 text-sm',
          // Surface
          'bg-surface-glass backdrop-blur-sm',
          'ring-1 ring-surface-stroke',
          // Text
          'text-text-1',
          // Focus
          'focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-0',
          // Transitions
          'transition-shadow duration-150',
          // Disabled
          'disabled:opacity-40 disabled:cursor-not-allowed',
          className,
        )}
        {...props}
      >
        {children}
      </select>
      {/* Custom chevron — sits on top of the native arrow (which is hidden by appearance-none) */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-text-3"
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M2.5 4.5L6 8l3.5-3.5" />
        </svg>
      </span>
    </div>
  );
});
